import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import { DATE_TIME_FORMAT_DTP } from "../../../Utils/Constants";

const DateTimePicker = ({
  value,
  onChange,
  id,
  placeholder,
  className,
  disabled,
  minDate,
}) => {
  const selectedDate = useMemo(() => {
    if (!value) return null;

    const parsed = moment(value, DATE_TIME_FORMAT_DTP, true);
    if (!parsed.isValid()) return null;

    return parsed.toDate();
  }, [value]);

  const minDateTime = useMemo(() => {
    if (!minDate) return null;

    const parsedMinDate = minDate instanceof Date ? new Date(minDate) : new Date(minDate);
    return Number.isNaN(parsedMinDate.getTime()) ? null : parsedMinDate;
  }, [minDate]);

  const isSelectedToday = useMemo(() => {
    if (!selectedDate || !minDateTime) return false;

    return selectedDate.toDateString() === minDateTime.toDateString();
  }, [selectedDate, minDateTime]);

  const minSelectableTime = useMemo(() => {
    if (!isSelectedToday || !minDateTime) return undefined;

    return minDateTime;
  }, [isSelectedToday, minDateTime]);

  const maxSelectableTime = useMemo(() => {
    const baseDate = selectedDate || minDateTime;
    if (!baseDate) return undefined;

    const endOfDay = new Date(baseDate);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }, [selectedDate, minDateTime]);

  const timeConstraintProps = useMemo(() => {
    if (!minSelectableTime || !maxSelectableTime) {
      return {};
    }

    return {
      minTime: minSelectableTime,
      maxTime: maxSelectableTime,
    };
  }, [minSelectableTime, maxSelectableTime]);

  const includedTimes = useMemo(() => {
    const baseDate = selectedDate || minDateTime;
    if (!baseDate) return undefined;

    const dayStart = new Date(baseDate);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(baseDate);
    dayEnd.setHours(23, 45, 0, 0);

    const firstAllowed = minSelectableTime ? new Date(minSelectableTime) : new Date(dayStart);
    firstAllowed.setSeconds(0, 0);

    const remainder = firstAllowed.getMinutes() % 15;
    if (remainder !== 0) {
      firstAllowed.setMinutes(firstAllowed.getMinutes() + (15 - remainder));
    }

    if (firstAllowed < dayStart) {
      firstAllowed.setTime(dayStart.getTime());
    }

    const options = [];
    const cursor = new Date(firstAllowed);

    while (cursor <= dayEnd) {
      options.push(new Date(cursor));
      cursor.setMinutes(cursor.getMinutes() + 15);
    }

    return options;
  }, [selectedDate, minDateTime, minSelectableTime]);

  const filterTime = (time) => {
    if (!minDateTime || !selectedDate) return true;

    if (selectedDate.toDateString() !== minDateTime.toDateString()) {
      return true;
    }

    return time.getTime() >= minDateTime.getTime();
  };

  const handleChange = (date) => {
    if (!date) {
      onChange("");
      return;
    }

    const formatted = moment(date).format(DATE_TIME_FORMAT_DTP);
    onChange(formatted);
  };

  return (
    <DatePicker
      id={id}
      selected={selectedDate}
      onChange={handleChange}
      placeholderText={placeholder}
      className={className}
      showTimeSelect
      timeIntervals={15}
      dateFormat="MM/dd/yyyy, h:mm aa"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      minDate={minDate}
      filterTime={filterTime}
      includeTimes={includedTimes}
      disabled={disabled}
      popperPlacement="bottom-start"
      autoComplete="off"
      key={`${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : "empty"}-${minDateTime ? moment(minDateTime).format("YYYY-MM-DD-HH-mm") : "no-min"}`}
      {...timeConstraintProps}
    />
  );
};

export default DateTimePicker;
